import { useContext, useState } from "react";
import { MenuItem } from "@mui/material";
import { DataContext, GroupContext } from "../../lib/contexts";
import { categoryToIcon } from "../../lib/definitions";
import { callEndpoint, capitalizeFirstLetter } from "../../lib/functions";
import { categoryToColor } from "../../lib/lib";
import { MInput } from "../elements/mInputs";
import { MSelect } from "../elements/mselect";
import { OrganicCheckbox } from "../elements/organicCheckbox";
import {
  CreateItemEndpoint,
  Item,
  ItemCategory,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { useNotifcation } from "../../lib/hooks/useNotification";
import { PlusOutlined } from "@ant-design/icons";
import { MButton } from "../elements/mbutton";
import { ModalActionContext } from "../../lib/hooks/useModalAction";
import css from "./createItemContent.module.css";

interface CreateItemContentProps {
  initialItemName: string;
  onFinish(itemId: string, isOther?: number): void;
  onClose(): void;
  allowOther?: boolean;
}

export function CreateItemContent(props: CreateItemContentProps) {
  const [itemName, setItemName] = useState(
    capitalizeFirstLetter(props.initialItemName),
  );
  const [selectedCategory, setSelectedCategory] = useState<ItemCategory>();
  const [isOrganic, setIsOrganic] = useState(false);

  const { refreshItems, updateItems, items } = useContext(DataContext);
  const { group } = useContext(GroupContext);
  const { isBusy } = useContext(ModalActionContext);
  const notify = useNotifcation();

  const categories = Object.values(ItemCategory).filter(
    (category) => props.allowOther !== false || category !== ItemCategory.Other,
  );
  const canSubmit = !!selectedCategory && !!itemName.trim() && !!group;

  const handleSubmit = async () => {
    if (!canSubmit || !group) {
      return;
    }

    const item = {
      name: capitalizeFirstLetter(itemName.trim()),
      category: selectedCategory,
      isOrganic,
    };

    if (selectedCategory !== ItemCategory.Other) {
      const [err, data, { response }] = await callEndpoint(
        CreateItemEndpoint,
        item,
      );

      if (err) {
        if (response?.status === 400) {
          notify("Oprettelse af vare: Denne vare findes allerede");
        } else {
          notify("Oprettelse af vare: Der skete en fejl");
        }
        return;
      }

      await updateItems(
        (items as Item[]).concat({
          _id: data.id,
          groupShortId: group.shortId,
          ...item,
        }),
      );
      await refreshItems(true);
      props.onFinish(data.id);
    } else {
      props.onFinish(item.name, isOrganic ? 2 : 1);
    }
  };

  return (
    <div className={css.form}>
      <MInput
        value={itemName}
        onChange={(event) => setItemName(event.target.value)}
        size="large"
        placeholder="Varens navn..."
        disabled={isBusy}
      />
      <MSelect<ItemCategory | "">
        size="large"
        value={selectedCategory || ""}
        displayEmpty
        disabled={isBusy}
        inputProps={{ "aria-label": "Kategori" }}
        onChange={(event) =>
          setSelectedCategory(event.target.value as ItemCategory)
        }
      >
        <MenuItem value="" disabled>
          Vælg kategori
        </MenuItem>
        {categories.map((category) => (
          <MenuItem key={category} value={category}>
            <span className={css.categoryLabel}>
              <span
                className={css.categoryIcon}
                style={{
                  background: `color-mix(in srgb, ${categoryToColor[category]} 15%, transparent)`,
                }}
              >
                {categoryToIcon[category]}
              </span>
              <span>{category}</span>
            </span>
          </MenuItem>
        ))}
      </MSelect>
      <OrganicCheckbox checked={isOrganic} onChange={setIsOrganic} />
      <div className={css.footer}>
        <MButton type="ghost" onClick={props.onClose}>
          Annuller
        </MButton>
        <MButton
          type="primary"
          disabled={!canSubmit}
          onClick={handleSubmit}
          icon={<PlusOutlined />}
        >
          {selectedCategory === ItemCategory.Other ? "Tilføj" : "Opret"}
        </MButton>
      </div>
    </div>
  );
}
